<template>
  <div class="audit-logs-advanced-filters">
    <div class="flex flex-col items-start pt-6 px-6 pb-4" style="box-shadow: 0px 1px 5px 1px #dfdfdf;">
      <div class="text-slate-800 text-lg dark:text-slate-100 font-semibold">
        {{ $t('AUDIT_LOGS_FILTER.TITLE') }}
      </div>
      <div class="text-sm text-slate-700 dark:text-slate-200">
        {{ $t('AUDIT_LOGS_FILTER.SUBTITLE') }}
      </div>
      <span class="text-slate-400 dark:text-slate-200 text-xs italic mt-1">
        *{{
          $t('AUDIT_LOGS_FILTER.SUBTITLE_OTHER', {
            maxFilterElement: MAX_FILTERS - 1,
          })
        }}
      </span>
    </div>

    <div
      ref="filtersBody"
      class="filters-body px-6 py-6 min-h-[260px] max-h-[600px]"
      :class="{ 'overflow-y-auto': isOverflowing }"
    >
      <filter-input-box
        v-for="(filter, i) in appliedFilters"
        :key="i"
        v-model="appliedFilters[i]"
        :filter-groups="filterGroups"
        :grouped-filters="true"
        :input-type="getInputType(filter.attribute_key)"
        :operators="getOperators(filter.attribute_key)"
        :dropdown-values="getDropdownValues(filter.attribute_key)"
        :show-query-operator="i !== appliedFilters.length - 1"
        :show-user-input="shouldShowUserInput(filter.filter_operator)"
        :show-filter-operator="filter.showFilterOperator"
        :show-filter-type="filter.showFilterType"
        :show-input-label="filter.showInputLabel"
        :v="$v.appliedFilters.$each[i]"
        :query-operators="filter.queryOperators"
        :disable-remove-filter="appliedFilters.length === 1"
        @resetFilter="resetFilter(i, filter)"
        @removeFilter="removeFilter(i)"
      />
      <div :class="appliedFilters.length == 1 ? 'mt-[60px]' : 'mt-4'">
        <div
          class="add-filter-btn button-outline flex items-center px-3 py-[7px] w-fit"
          :class="{ disabled: hasReachedMaxFilters }"
          :disabled="hasReachedMaxFilters"
          @click="appendNewFilter"
        >
          <span class="material-symbols-outlined text-[24px]/[24px]">
            add
          </span>
          <span class="text-sm">
            {{ $t('AUDIT_LOGS_FILTER.ADD_DATE_FILTER') }}
          </span>
        </div>
        <woot-button
          v-if="hasAppliedFilters"
          icon="subtract"
          color-scheme="alert"
          variant="smooth"
          @click="clearFilters"
        >
          {{ $t('AUDIT_LOGS_FILTER.CLEAR_ALL_FILTERS') }}
        </woot-button>
      </div>
      <p
        v-if="hasReachedMaxFilters"
        class="text-xs text-yellow-600 dark:text-yellow-400 mt-2"
      >
        {{
          $t('AUDIT_LOGS_FILTER.MAX_FILTER_REACHED', {
            maxFilters: MAX_FILTERS,
          })
        }}
      </p>
    </div>
    <div
      v-if="!skipButtons"
      class="w-full px-6 py-4"
      style="box-shadow: 0px -1px 5px 0px #dfdfdf"
    >
      <div class="flex flex-row justify-end gap-3 px-0 w-full">
        <woot-button
          class="cancel-filter-btn button clear text-base px-5 py-3"
          color-scheme="secondary"
          @click.prevent="onClose"
        >
          {{ $t('AUDIT_LOGS_FILTER.CANCEL_BUTTON_LABEL') }}
        </woot-button>
        <woot-button
          class="apply-filter-btn button-filled text-base px-5 py-3"
          color-scheme=""
          :is-disabled="!hasAppliedFilters && !hasValidFilters"
          @click="submitFilterQuery"
        >
          {{ $t('AUDIT_LOGS_FILTER.APPLY_BUTTON_LABEL') }}
        </woot-button>
      </div>
    </div>
  </div>
</template>

<script>
import alertMixin from 'shared/mixins/alertMixin';
import { required, requiredIf } from 'vuelidate/lib/validators';
import FilterInputBox from '../../../../components/widgets/FilterInput/IndexV2.vue';
import { mapGetters } from 'vuex';
import { filterAttributeGroups } from './auditlogsFilterItems';
import { auditLogTypes } from './auditlogsTypes';
import filterMixin from 'shared/mixins/filterMixin';
import { AUDITLOGS_EVENTS } from '../../../../helper/AnalyticsHelper/events';

const MAX_FILTERS = 4;

const QUERY_OPERATORS = {
  AND: [{ value: 'and', label: 'FILTER.QUERY_DROPDOWN_LABELS.AND' }],
  OR: [{ value: 'or', label: 'FILTER.QUERY_DROPDOWN_LABELS.OR' }],
};

export default {
  components: {
    FilterInputBox,
  },
  mixins: [alertMixin, filterMixin],
  props: {
    onClose: {
      type: Function,
      default: () => {},
    },
    initialFilterTypes: {
      type: Array,
      default: () => [],
    },
    initialAppliedFilters: {
      type: Array,
      default: () => [],
    },
    skipHeader: {
      type: Boolean,
      default: false,
    },
    skipButtons: {
      type: Boolean,
      default: false,
    },
  },
  validations: {
    appliedFilters: {
      required,
      $each: {
        values: {
          validDateRange(value, prop) {
            if (prop.filter_operator !== 'is_between') {
              return true;
            }

            if (
              !value ||
              typeof value !== 'object' ||
              !value.from ||
              !value.to
            ) {
              return false;
            }

            return this.isValidDateRange(value.from, value.to);
          },
          required: requiredIf(prop => prop.attribute_key !== 'action'),
        },
      },
    },
  },
  data() {
    return {
      show: true,
      appliedFilters: [],
      filterTypes: this.initialFilterTypes,
      filterGroups: [],
      allCustomAttributes: [],
      filterAttributeGroups,
      filtersFori18n: 'AUDIT_LOGS_FILTER',
      MAX_FILTERS,
      isOverflowing: false,
    };
  },
  computed: {
    ...mapGetters({
      getAppliedAuditlogsFilters: 'auditlogs/getAppliedAuditlogsFilters',
    }),
    hasAppliedFilters() {
      return this.getAppliedAuditlogsFilters.length > 0;
    },
    hasReachedMaxFilters() {
      return this.appliedFilters.length >= MAX_FILTERS;
    },
    hasValidFilters() {
      return this.appliedFilters.some(filter => {
        if (Array.isArray(filter.values)) {
          return filter.values.length > 0;
        }
        if (filter.values && typeof filter.values === 'object') {
          return filter.values.from || filter.values.to;
        }
        return filter.values !== '' && filter.values != null;
      });
    },
  },
  watch: {
    appliedFilters: {
      handler(value) {
        this.$emit('onFiltersChange', value);
      },
      deep: true,
    },
  },
  mounted() {
    this.setFilterAttributes();
    this.initializeFilters();
  },
  methods: {
    initializeFilters() {
      if (this.getAppliedAuditlogsFilters.length) {
        this.appliedFilters = [...this.getAppliedAuditlogsFilters];
      } else if (this.initialAppliedFilters.length) {
        this.appliedFilters = [...this.initialAppliedFilters];
      } else {
        this.appliedFilters = [this.createDefaultActionFilter()];
      }
    },
    createDefaultActionFilter() {
      return {
        attribute_key: 'action',
        filter_operator: 'equal_to',
        values: '',
        query_operator: 'and',
        attribute_model: 'associated',
        queryOperators: QUERY_OPERATORS.AND,
        showInputLabel: true,
      };
    },
    createDateRangeFilter() {
      return {
        attribute_key: 'created_at',
        filter_operator: 'is_between',
        values: { from: '', to: '' },
        query_operator: 'or',
        inputType: 'date_range',
        showFilterOperator: false,
        showFilterType: false,
        showInputLabel: true,
        queryOperators: QUERY_OPERATORS.OR,
      };
    },
    isValidDateRange(fromDateStr, toDateStr) {
      const [fromDay, fromMonth, fromYear] = fromDateStr.split('-');
      const [toDay, toMonth, toYear] = toDateStr.split('-');
      const fromDate = new Date(fromYear, fromMonth - 1, fromDay);
      const toDate = new Date(toYear, toMonth - 1, toDay);
      return fromDate <= toDate;
    },
    getAttributeModel(key) {
      const type = this.filterTypes.find(filter => filter.attributeKey === key);
      return type?.attributeModel;
    },
    getInputType(key) {
      if (key === 'created_at') return 'date_range';
      const type = this.filterTypes.find(filter => filter.attributeKey === key);
      return type?.inputType;
    },
    getOperators(key) {
      const type = this.filterTypes.find(filter => filter.attributeKey === key);
      return type?.filterOperators;
    },
    getDropdownValues(type) {
      const dropdownMap = {
        action: auditLogTypes.map(i => ({ ...i, name: this.$t(i.name) })),
      };
      return dropdownMap[type];
    },
    appendNewFilter() {
      if (this.hasReachedMaxFilters) {
        return;
      }
      this.appliedFilters.push(this.createDateRangeFilter());
      this.checkOverflow();
    },
    removeFilter(index) {
      if (this.appliedFilters.length <= 1) {
        this.showAlert(this.$t('AUDIT_LOGS_FILTER.FILTER_DELETE_ERROR'));
        return;
      }
      this.appliedFilters.splice(index, 1);
      this.checkOverflow();
    },
    resetFilter(index, currentFilter) {
      const filterType = this.filterTypes.find(
        filter => filter.attributeKey === currentFilter.attribute_key
      );

      if (!filterType) return;

      const resetValues =
        filterType.inputType === 'date_range' ? { from: '', to: '' } : '';

      this.appliedFilters[index] = {
        ...this.appliedFilters[index],
        ...filterType,
        filter_operator: filterType.filterOperators[0].value,
        values: resetValues,
      };
    },
    checkOverflow() {
      const el = this.$refs.filtersBody;
      if (el) {
        this.isOverflowing =
          el.scrollHeight > 500 && this.appliedFilters.length > 1;
      }
    },
    shouldShowUserInput(operatorType) {
      return operatorType !== 'is_present' && operatorType !== 'is_not_present';
    },
    isFiltersValid() {
      this.$v.$touch();
      return !this.$v.$invalid;
    },
    submitFilterQuery() {
      if (!this.isFiltersValid()) return;

      const filtersClone = JSON.parse(JSON.stringify(this.appliedFilters));

      this.$store.dispatch('auditlogs/setAuditLogsFilters', filtersClone);

      this.$track(AUDITLOGS_EVENTS.APPLY_FILTER, {
        applied_filters: this.appliedFilters.map(filter => ({
          key: filter.attribute_key,
          operator: filter.filter_operator,
          query_operator: filter.query_operator,
        })),
      });

      this.$emit('applyFilter', this.appliedFilters);
    },
    clearFilters() {
      this.$emit('clearFilters');
    },
  },
};
</script>

<style lang="scss">
.audit-advanced-filter-modal {
  .modal-container {
    @apply rounded-[12px];

    .woot-modal-header {
      @apply pt-6 px-6;
    }
  }
}
</style>

<style lang="scss" scoped>
::v-deep {
  .apply-filter-btn,
  .cancel-filter-btn {
    height: unset;
    .button__content {
      line-height: 22px !important;
    }
  }
}
.filters-body {
  scrollbar-width: thin;
  scrollbar-color: #d1d5db transparent;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    @apply bg-gray-300 rounded-full;
  }

  &::-webkit-scrollbar-thumb:hover {
    @apply bg-gray-400;
  }
}
</style>
